import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/auth-context'
import { dashboardService, type DashboardStats } from '@/lib/api/services/dashboard'
import { analyticsService, type ActivityLog } from '@/lib/api/services/analytics'
import {
  FileText,
  FolderOpen,
  Users,
  Activity,
  Sparkles,
  ArrowRight,
  TrendingUp,
  Clock,
  Plus,
} from 'lucide-react'
import { FluentCard, StatCard } from '@/components/layout/page-header'

export default function Dashboard() {
  const { user } = useAuth()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentActivity, setRecentActivity] = useState<ActivityLog[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [statsData, analyticsData] = await Promise.allSettled([
        dashboardService.getStats(),
        analyticsService.getAnalytics()
      ])

      if (statsData.status === 'fulfilled') {
        setStats(statsData.value)
      }
      if (analyticsData.status === 'fulfilled') {
        setRecentActivity(analyticsData.value.recentActivity?.slice(0, 4) || [])
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const statCards = [
    {
      label: 'Total Templates',
      value: stats?.totalTemplates ?? 0,
      icon: FileText,
      color: 'from-blue-400 to-blue-600',
    },
    {
      label: 'Digital Assets',
      value: stats?.totalAssets ?? 0,
      icon: FolderOpen,
      color: 'from-emerald-400 to-green-600',
    },
    {
      label: 'Team Members',
      value: stats?.totalUsers ?? 0,
      icon: Users,
      color: 'from-violet-400 to-purple-600',
    },
    {
      label: 'Active Users',
      value: stats?.activeUsers ?? 0,
      icon: Sparkles,
      color: 'from-amber-400 to-orange-600',
    },
  ]

  const quickActions = [
    {
      label: 'Create Template',
      description: 'Start a new document template',
      icon: FileText,
      href: '/templates/new',
      color: 'from-blue-400 to-blue-600',
    },
    {
      label: 'Upload Asset',
      description: 'Add files to your library',
      icon: FolderOpen,
      href: '/assets',
      color: 'from-emerald-400 to-green-600',
    },
    {
      label: 'AI Studio',
      description: 'Generate content with AI',
      icon: Sparkles,
      href: '/ai-studio',
      color: 'from-amber-400 to-orange-600',
    },
    {
      label: 'Invite Team',
      description: 'Add team members',
      icon: Users,
      href: '/admin/users',
      color: 'from-violet-400 to-purple-600',
    },
  ]

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-none bg-gradient-to-r from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900 p-8 lg:p-10">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-400 mb-2">
            Welcome back
          </p>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
            Hello, {user?.firstName || 'there'}!
          </h1>
          <p className="text-gray-400 max-w-xl">
            Here's what's happening with your templates and documents today.
            You have <span className="text-white font-medium">{stats?.totalTemplates ?? 0} templates</span> and{' '}
            <span className="text-white font-medium">{stats?.totalAssets ?? 0} assets</span> in your workspace.
          </p>

          <div className="flex flex-wrap gap-3 mt-6">
            <Link
              to="/templates/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Template
            </Link>
            <Link
              to="/ai-studio"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 text-white text-sm font-medium rounded-full hover:bg-white/20 transition-colors border border-white/20"
            >
              <Sparkles className="h-4 w-4" />
              Open AI Studio
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <StatCard
            key={stat.label}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
            loading={loading}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Quick Actions */}
        <FluentCard className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Quick Actions
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Common tasks to get you started
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.href}
                className="group flex items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              >
                <div className={`p-3 rounded-xl bg-gradient-to-br ${action.color} shadow-lg`}>
                  <action.icon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{action.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{action.description}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
              </Link>
            ))}
          </div>
        </FluentCard>

        {/* Recent Activity */}
        <FluentCard>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recent Activity
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your latest actions
              </p>
            </div>
            <Link
              to="/admin/analytics"
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              View all
            </Link>
          </div>

          <div className="space-y-4">
            {recentActivity.length > 0 ? (
              recentActivity.map((item) => (
                <div key={item.id} className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                    <Activity className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {item.action}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {item.entityName || item.userName}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock className="h-3 w-3" />
                    {new Date(item.timestamp).toLocaleDateString()}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Activity className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No recent activity
                </p>
              </div>
            )}
          </div>
        </FluentCard>
      </div>

      {/* Usage Analytics */}
      <FluentCard>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Usage Analytics
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your activity over the last 30 days
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-gray-900 dark:focus:ring-white">
              <option value="7">Last 7 days</option>
              <option value="30" selected>Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
        </div>

        {/* Placeholder chart area */}
        <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl">
          <div className="text-center">
            <TrendingUp className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              View detailed analytics on the{' '}
              <Link to="/admin/analytics" className="text-gray-900 dark:text-white hover:underline">
                Analytics page
              </Link>
            </p>
          </div>
        </div>
      </FluentCard>
    </div>
  )
}
